import { isEmpty, isEqual } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API } from '@shared/types/api';
import { useStableValue } from '@shared/util/hooks';
import { omitTypeName } from '@shared/util/omitTypeName';
import { type EditableViewTemplate, getEditableViewTemplate } from './EditableViewTemplate';
import { PreserveViewTemplate } from './PreserveViewTemplate';
import {
  PreserveEditsProvider,
  ViewTemplateActionsProvider,
  ViewTemplateStateProvider,
} from './ViewTemplateContext';
import { useViewTemplateQueryParamOverrides } from './collectionViewQueryParamOverrides';
import { useSaveViewTemplate } from './useSaveViewTemplate';

interface ViewTemplateProviderProps {
  children: ReactNode;
  workflowTemplateId: string;
  viewTemplate: API.ViewTemplateFragment;
  /**
   * Whether unsaved filter/sort/group edits should be persisted to local storage and restored on
   * mount. Only enable this for the primary collection view page a user browses and edits
   * directly. Defaults to false.
   */
  preserveEdits?: boolean;
}

export function ViewTemplateProvider({
  children,
  workflowTemplateId,
  viewTemplate: inputViewTemplate,
  preserveEdits = false,
}: ViewTemplateProviderProps) {
  const [savedViewTemplate, setSavedViewTemplate] = useStableValue(omitTypeName(inputViewTemplate));
  const [viewTemplate, setViewTemplate] = useStableValue(savedViewTemplate);
  const saveViewTemplate = useSaveViewTemplate(workflowTemplateId, setViewTemplate);
  const [isModified, setIsModified] = useState(false);
  const queryParamOverrides = useViewTemplateQueryParamOverrides(viewTemplate);
  const hasQueryParamOverrides = !isEmpty(queryParamOverrides);
  // The override value itself (e.g. calendarDateComponentId) is read once at mount and stays
  // stable (see `useViewTemplateQueryParamOverrides`), but whether it *applies* can still change
  // mid-session - e.g. calendarDateComponentId only applies while subtype is CALENDAR, so editing
  // the subtype away would flip `hasQueryParamOverrides` back to false. Latch it so the
  // sync-with-saved effect below keeps treating the override as authoritative for the rest of
  // this mount, rather than reverting back to `savedViewTemplate` the moment it stops applying.
  const hadQueryParamOverridesRef = useRef(hasQueryParamOverrides);
  if (hasQueryParamOverrides) {
    hadQueryParamOverridesRef.current = true;
  }

  useEffect(() => {
    // reset savedViewTemplate when inputViewTemplate changes
    setSavedViewTemplate(omitTypeName(inputViewTemplate));
  }, [inputViewTemplate, setSavedViewTemplate]);

  const previousSavedViewTemplateRef = useRef(savedViewTemplate);

  useEffect(() => {
    const previousSavedViewTemplate = previousSavedViewTemplateRef.current;
    previousSavedViewTemplateRef.current = savedViewTemplate;
    if (hadQueryParamOverridesRef.current) {
      // A query param override takes precedence and is applied by PreserveViewTemplate instead;
      // don't let this sync clobber it.
      return;
    }
    const updates = getViewTemplateUpdates(savedViewTemplate, previousSavedViewTemplate);
    if (!isEmpty(updates)) {
      setViewTemplate(prev => ({ ...prev, ...updates }));
    }
  }, [savedViewTemplate, setViewTemplate]);

  const revertViewTemplate = useCallback(() => {
    // accessing current savedViewTemplate via state-setter, so this callback
    // can be stateless
    setSavedViewTemplate(saved => {
      setViewTemplate(saved);
      return saved;
    });
  }, [setViewTemplate, setSavedViewTemplate]);

  const actions = useMemo(
    () => ({ setViewTemplate, revertViewTemplate, saveViewTemplate }),
    [setViewTemplate, revertViewTemplate, saveViewTemplate],
  );

  // preserveEdits lives in its own context: it flips on every card modal
  // open/close, and as part of this state value that flip re-rendered every
  // useViewTemplateState consumer (each group's card service included)
  const state = useMemo(
    () => ({ viewTemplate, savedViewTemplate, isModified }),
    [viewTemplate, savedViewTemplate, isModified],
  );

  return (
    <ViewTemplateActionsProvider value={actions}>
      <ViewTemplateStateProvider value={state}>
        <PreserveEditsProvider value={preserveEdits}>
          {children}
          <PreserveViewTemplate
            setIsModified={setIsModified}
            queryParamOverrides={queryParamOverrides}
          />
        </PreserveEditsProvider>
      </ViewTemplateStateProvider>
    </ViewTemplateActionsProvider>
  );
}

function getViewTemplateUpdates(
  savedViewTemplate: API.ViewTemplateFragment,
  currrentViewTemplate: API.ViewTemplateFragment,
): Partial<EditableViewTemplate> {
  const editableSaved = getEditableViewTemplate(savedViewTemplate);
  const editableCurrent = getEditableViewTemplate(currrentViewTemplate);
  const entries = Object.entries(editableSaved) as [keyof EditableViewTemplate, unknown][];
  return entries.reduce((memo: Partial<EditableViewTemplate>, [key, value]) => {
    if (!isEqual(editableCurrent[key], value)) {
      return { ...memo, [key]: value };
    } else {
      return memo;
    }
  }, {});
}
